import React, { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Table from '../components/Table';
import Modal from '../components/Modal';
import { Printer, RefreshCw, MapPin, DollarSign, Filter } from 'lucide-react';

export default function CobrancaPresencial() {
  const { getRelatorioPresencial, getClientesEmAtraso } = useApi();
  const [clientes, setClientes] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Filtros de dias de atraso (padrão 90+)
  const [diasMin, setDiasMin] = useState(90);
  const [diasMax, setDiasMax] = useState(9999);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Aplicar filtro quando os dias mudarem
    aplicarFiltro();
  }, [diasMin, diasMax, clientes]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Buscar clientes com 90+ dias
      const response = await getClientesEmAtraso(90);
      setClientes(response.clientes || []);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      alert('Erro ao carregar relatório presencial');
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltro = () => {
    const filtrados = clientes.filter(
      c => c.dias_atraso >= diasMin && c.dias_atraso <= diasMax
    );
    setClientesFiltrados(filtrados);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const columns = [
    {
      header: 'Cliente',
      accessor: 'nome_cliente'
    },
    {
      header: 'Endereço',
      accessor: 'endereco',
      render: (row) => (
        <div className="text-sm">
          <p>{row.endereco}, {row.numero}</p>
          <p className="text-gray-500">{row.bairro} - {row.cidade}/{row.estado}</p>
        </div>
      )
    },
    {
      header: 'Dias em Atraso',
      accessor: 'dias_atraso',
      render: (row) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          {row.dias_atraso} dias
        </span>
      )
    },
    {
      header: 'Valor Total',
      accessor: 'valor_total',
      render: (row) => (
        <span className="font-semibold text-red-600">
          {formatCurrency(row.valor_total || row.valor_em_aberto)}
        </span>
      )
    },
    {
      header: 'Ações',
      render: (row) => (
        <button
          onClick={() => {
            setSelectedCliente(row);
            setShowModal(true);
          }}
          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
        >
          Ver Detalhes
        </button>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Carregando relatório..." />
      </div>
    );
  }

  const valorTotal = clientesFiltrados.reduce((sum, c) => sum + (c.valor_total || c.valor_em_aberto || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header - Não imprimir */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cobrança Presencial</h1>
          <p className="text-gray-600 mt-1">Lista de clientes para visita presencial</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Imprimir Lista
          </button>
        </div>
      </div>

      {/* Filtros de Dias de Atraso */}
      <div className="bg-white rounded-lg shadow p-6 print:hidden">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filtro de Período</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dias mínimos de atraso
            </label>
            <input
              type="number"
              value={diasMin}
              onChange={(e) => setDiasMin(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dias máximos de atraso
            </label>
            <input
              type="number"
              value={diasMax}
              onChange={(e) => setDiasMax(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              min="0"
              placeholder="Sem limite"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={aplicarFiltro}
              className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Aplicar Filtro
            </button>
          </div>
        </div>

        <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-800">
            <strong>Filtro ativo:</strong> Mostrando clientes com {diasMin}+ dias de atraso
            {diasMin === 90 && diasMax === 9999 && " (padrão)"}
          </p>
        </div>
      </div>

      {/* Resumo - Não imprimir */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:hidden">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Total de Clientes</p>
          <p className="text-2xl font-bold text-gray-900">{clientesFiltrados.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Valor Total a Cobrar</p>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(valorTotal)}</p>
        </div>
      </div>

      {/* Tabela - Mostrar na tela */}
      <div className="bg-white rounded-lg shadow overflow-hidden print:hidden">
        <Table
          columns={columns}
          data={clientesFiltrados}
          emptyMessage="Nenhum cliente encontrado neste período"
        />
      </div>

      {/* Lista para impressão - Oculta na tela */}
      <div className="hidden print:block">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold">LISTA DE COBRANÇA PRESENCIAL</h1>
          <p className="text-gray-600 mt-2">
            Data: {new Date().toLocaleDateString('pt-BR')}
          </p>
          <p className="text-gray-600">
            Período: {diasMin}+ dias de atraso
          </p>
          <p className="text-gray-600">
            Total: {clientesFiltrados.length} clientes | Valor: {formatCurrency(valorTotal)}
          </p>
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-gray-800">
              <th className="text-left py-2 px-2">Nº</th>
              <th className="text-left py-2 px-2">Cliente</th>
              <th className="text-left py-2 px-2">Endereço</th>
              <th className="text-right py-2 px-2">Valor</th>
              <th className="text-center py-2 px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {clientesFiltrados.map((cliente, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="py-3 px-2">{index + 1}</td>
                <td className="py-3 px-2">
                  <div>
                    <p className="font-semibold">{cliente.nome_cliente}</p>
                    <p className="text-sm text-gray-600">
                      {cliente.telefone || 'Sem telefone'}
                    </p>
                  </div>
                </td>
                <td className="py-3 px-2">
                  <div className="text-sm">
                    <p>{cliente.endereco}, {cliente.numero}</p>
                    <p>{cliente.bairro} - {cliente.cidade}/{cliente.estado}</p>
                    {cliente.cep && <p>CEP: {cliente.cep}</p>}
                  </div>
                </td>
                <td className="py-3 px-2 text-right font-semibold">
                  {formatCurrency(cliente.valor_total || cliente.valor_em_aberto)}
                </td>
                <td className="py-3 px-2 text-center">
                  <div className="border border-gray-400 w-6 h-6 mx-auto"></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-8 border-t-2 border-gray-800 pt-4">
          <p className="text-sm">Observações:</p>
          <div className="border border-gray-400 h-20 mt-2"></div>
        </div>
      </div>

      {/* Modal de Detalhes */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Detalhes do Cliente"
        size="lg"
      >
        {selectedCliente && (
          <div className="space-y-4">
            {/* Informações do Cliente */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Informações do Cliente</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">Nome:</p>
                  <p className="font-medium">{selectedCliente.nome_cliente}</p>
                </div>
                <div>
                  <p className="text-gray-600">Telefone:</p>
                  <p className="font-medium">{selectedCliente.telefone || 'Não cadastrado'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-600">Endereço:</p>
                  <p className="font-medium">
                    {selectedCliente.endereco}, {selectedCliente.numero}
                    {selectedCliente.complemento && ` - ${selectedCliente.complemento}`}
                  </p>
                  <p className="font-medium">
                    {selectedCliente.bairro} - {selectedCliente.cidade}/{selectedCliente.estado}
                  </p>
                  {selectedCliente.cep && <p className="font-medium">CEP: {selectedCliente.cep}</p>}
                </div>
              </div>
            </div>

            {/* Resumo Financeiro */}
            <div className="bg-red-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Resumo Financeiro</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Dias em Atraso:</span>
                  <span className="font-semibold text-red-600">{selectedCliente.dias_atraso} dias</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Quantidade de Contas:</span>
                  <span className="font-semibold">{selectedCliente.contas?.length || 0}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span className="font-semibold text-gray-900">Valor Total:</span>
                  <span className="font-bold text-red-600">
                    {formatCurrency(selectedCliente.valor_total || selectedCliente.valor_em_aberto)}
                  </span>
                </div>
              </div>
            </div>

            {/* Lista de Contas */}
            {selectedCliente.contas && selectedCliente.contas.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Contas em Aberto</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedCliente.contas.map((conta, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm font-medium">{conta.descricao}</p>
                        <p className="text-xs text-gray-600">
                          Vencimento: {new Date(conta.data_vencimento).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <span className="font-semibold text-red-600">
                        {formatCurrency(conta.valor_em_aberto)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}